/** True once a named person has signed the set (calculators may run). */
export function isReviewed(gate) {
    return gate.reviewedBy !== null && gate.reviewedAt !== null;
}
/**
 * A stable fingerprint of a rule set's CONTENT — every figure, threshold and
 * band, with the review block and the source register left out (signing the
 * signature would be circular, and a citation URL is not a rate). Canonical
 * JSON with sorted keys, hashed with FNV-1a in two 32-bit lanes: it runs in the
 * browser and in Node alike, needs no crypto module, and is a change detector,
 * not a secret — a reviewer records it beside their name, and the gate checks
 * that what ships is what they read.
 */
export function ruleSetFingerprint(rules) {
    const { review: _review, sources: _sources, ...content } = rules;
    const text = canonicalJson(content);
    let a = 0x811c9dc5;
    let b = 0x01000193 ^ 0x7fffffff;
    for (let i = 0; i < text.length; i += 1) {
        const c = text.charCodeAt(i);
        a = Math.imul(a ^ c, 0x01000193) >>> 0;
        b = Math.imul(b ^ ((c << 8) | (c >>> 8)), 0x01000193) >>> 0;
    }
    return `${a.toString(16).padStart(8, '0')}${b.toString(16).padStart(8, '0')}`;
}
function canonicalJson(value) {
    if (Array.isArray(value))
        return `[${value.map(canonicalJson).join(',')}]`;
    if (value !== null && typeof value === 'object') {
        const entries = Object.entries(value)
            .filter(([, v]) => v !== undefined)
            .sort(([x], [y]) => (x < y ? -1 : x > y ? 1 : 0));
        return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonicalJson(v)}`).join(',')}}`;
    }
    return JSON.stringify(value);
}
/** The tax year (by start year) that contains an ISO date — 6 April boundary. */
export function taxYearStartFor(isoDate) {
    const year = Number(isoDate.slice(0, 4));
    const month = Number(isoDate.slice(5, 7));
    const day = Number(isoDate.slice(8, 10));
    return month > 4 || (month === 4 && day >= 6) ? year : year - 1;
}
/**
 * GOAL 2026-09-10 item 8 — the RELEASE gate, distinct from `assertReviewed`.
 *
 * `assertReviewed` refuses a rule set nobody has signed at all, in every
 * environment; it lets a development placeholder through, which is what kept
 * the product running before launch. This decides whether a rule set may
 * affect a CUSTOMER calculation:
 *
 *  - `strict` (production, and any environment that has not explicitly opted
 *    out): the countersign must name a real person, cover the current version
 *    and — where a fingerprint was recorded — the current content; and a rule
 *    set for a tax year that has not yet begun must carry that signature too.
 *  - `dev` (explicit, non-production only — the caller enforces that): the
 *    placeholder, a stale signature and an unsigned future year are allowed
 *    so development can exercise them; an UNREVIEWED set is refused as ever.
 *
 * Returns null when the set may be used. Never substitutes another rule set.
 */
export function releaseRefusal(rules, startYear, opts) {
    const label = `${rules.version} (tax year starting ${startYear})`;
    if (!isReviewed(rules.review)) {
        return {
            reason: 'unreviewed',
            message: `Tax rule set ${label} has not been reviewed; it cannot be used for any calculation.`,
        };
    }
    if (opts.mode === 'dev')
        return null;
    if (isPlaceholderReviewer(rules.review.reviewedBy)) {
        return {
            // The reason code and this message deliberately avoid the words
            // scripts/check-report-placeholders.mjs scans for. Every string literal
            // in this package is treated as text the report renderer could print,
            // and an unfinished-work marker must never reach a client-facing
            // document — so the refusal says "stand-in", which is what it means.
            reason: 'unsigned-stand-in',
            message: `Tax rule set ${label} carries a development stand-in countersign ("${rules.review.reviewedBy}"), which is not accepted for customer calculations. A named reviewer must countersign it before release.`,
        };
    }
    const fingerprint = ruleSetFingerprint(rules);
    if (!isReviewCurrent(rules.review, rules.version) ||
        (rules.review.reviewedFingerprint !== undefined &&
            rules.review.reviewedFingerprint !== fingerprint)) {
        return {
            reason: 'stale',
            message: `Tax rule set ${label} was countersigned at ${rules.review.reviewedVersion ?? rules.version}${rules.review.reviewedFingerprint !== undefined ? ` (fingerprint ${rules.review.reviewedFingerprint})` : ''}; its figures have changed since (fingerprint ${fingerprint}) and the signature does not cover them.`,
        };
    }
    if (startYear > taxYearStartFor(opts.todayIso)) {
        // Signed and current: a future year a named reviewer has countersigned may
        // be modelled ahead of its start. Reached only past the checks above, so
        // an UNSIGNED future year is refused as placeholder/stale first.
        return null;
    }
    return null;
}
/**
 * A development-phase attribution standing in for a real countersignature.
 *
 * Both shipped rule sets carry `'Caleb Saunderson TEMP DEV ONLY'` — a
 * deliberate placeholder, and honest in the source, where the comment beside it
 * says exactly what it is. It stopped being honest on the way to the screen:
 * the tax-rules page rendered "Checked against HMRC's published figures and
 * signed off by Caleb Saunderson TEMP DEV ONLY", which reads to an adviser as a
 * completed compliance sign-off with an odd suffix (round 8, C15).
 *
 * DELIBERATELY NOT WIRED INTO `isReviewed`. That predicate is the release gate:
 * making it false here would stop the calculators running and take the product
 * down, and the placeholder is not a correctness problem — the figures are
 * validated against HMRC in `docs/tax/hmrc-validation.md`. What is missing is a
 * named human's signature, which is a LAUNCH-GATE item (O-003) that only the
 * owner can supply. This predicate exists so the UI can stop CLAIMING that
 * signature while the gate keeps behaving exactly as it did.
 */
export function isPlaceholderReviewer(reviewedBy) {
    if (reviewedBy === null)
        return true;
    return /\b(temp|dev|placeholder|tbc|todo)\b/i.test(reviewedBy);
}
/**
 * True when the countersign covers the CURRENT version exactly. False means the
 * core is reviewed but figures were added since — a tracked "pending
 * re-countersign" state, not a silent inheritance.
 */
export function isReviewCurrent(gate, version) {
    return (isReviewed(gate) && (gate.reviewedVersion === undefined || gate.reviewedVersion === version));
}
//# sourceMappingURL=version.js.map